import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Download, FileText, BarChart3, Send } from 'lucide-react';

const ReportsPage = () => {
  const { token, user } = useContext(AuthContext);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/reports/daily-summary-pdf?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `daily_summary_${date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await axios.get(`${API}/reports/daily-summary-excel?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `daily_summary_${date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel downloaded successfully');
    } catch (error) {
      toast.error('Failed to download Excel');
    }
  };

  const sendDailySummary = async () => {
    try {
      await axios.post(`${API}/reports/send-daily-summary?date=${date}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Daily summary sent to EXCO team');
    } catch (error) {
      toast.error('Failed to send daily summary');
    }
  };

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/statistics?start_date=${startDate}&end_date=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load statistics:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reports-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900">Reports & Statistics</h1>
        <p className="text-sm sm:text-base text-slate-500 mt-2">Generate reports and view detailed statistics</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Daily Summary Reports */}
        <Card className="dashboard-card p-4 sm:p-6 flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 flex-1">
            <div className="p-3 rounded-md bg-blue-50 self-start">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1 flex flex-col">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3">Daily Summary Reports</h3>
              <div className="space-y-4 flex-1">
                <div>
                  <Label htmlFor="report-date" className="text-sm">Select Date</Label>
                  <Input
                    id="report-date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1.5"
                    data-testid="report-date-input"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <Button
                  onClick={downloadPDF}
                  variant="outline"
                  className="flex-1 flex items-center justify-center"
                  data-testid="download-pdf-button"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download PDF
                </Button>
                <Button
                  onClick={downloadExcel}
                  variant="outline"
                  className="flex-1 flex items-center justify-center"
                  data-testid="download-excel-button"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Excel
                </Button>
                {user?.role === 'admin' && (
                  <Button
                    onClick={sendDailySummary}
                    className="flex-1 bg-blue-900 hover:bg-blue-800"
                    data-testid="send-summary-button"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Send to EXCO
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Statistics */}
        <Card className="dashboard-card p-4 sm:p-6 flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 flex-1">
            <div className="p-3 rounded-md bg-emerald-50 self-start">
              <BarChart3 className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="flex-1 flex flex-col">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3">Detailed Statistics</h3>
              <div className="space-y-4 flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start-date" className="text-sm">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="mt-1.5"
                      data-testid="start-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-date" className="text-sm">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="mt-1.5"
                      data-testid="end-date-input"
                    />
                  </div>
                </div>
              </div>
              <Button
                onClick={fetchStatistics}
                disabled={loading}
                className="bg-blue-900 hover:bg-blue-800 w-full mt-4"
                data-testid="fetch-statistics-button"
              >
                {loading ? 'Loading...' : 'Generate Statistics'}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Statistics Results */}
      {statistics && (
        <div className="space-y-4 sm:space-y-6">
          <Card className="dashboard-card p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4">Statistics Summary</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-md">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Total Absences</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900">{statistics.total_absences}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-md">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Relief Assignments</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900">{statistics.total_relief_assignments}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-md">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Acknowledgment Rate</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900">{statistics.acknowledgment_rate}%</p>
              </div>
            </div>
          </Card>

          {/* Absence by Reason */}
          {Object.keys(statistics.absence_by_reason).length > 0 && (
            <Card className="dashboard-card p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Absences by Reason</h3>
              <div className="space-y-2">
                {Object.entries(statistics.absence_by_reason).map(([reason, count]) => (
                  <div key={reason} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                    <span className="text-sm font-medium text-slate-700">{reason}</span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-900 text-sm font-semibold rounded">{count}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Relief by Teacher */}
          {Object.keys(statistics.relief_by_teacher).length > 0 && (
            <Card className="dashboard-card p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Relief Workload by Teacher</h3>
              <div className="space-y-2">
                {Object.entries(statistics.relief_by_teacher)
                  .sort((a, b) => b[1] - a[1])
                  .map(([teacher, count]) => (
                    <div key={teacher} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                      <span className="text-sm font-medium text-slate-700">{teacher}</span>
                      <span className="px-3 py-1 bg-green-100 text-green-900 text-sm font-semibold rounded">{count}</span>
                    </div>
                  ))}
              </div>
            </Card>
          )}

          {/* Absence by Teacher */}
          {Object.keys(statistics.absence_by_teacher).length > 0 && (
            <Card className="dashboard-card p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Absences by Teacher</h3>
              <div className="space-y-2">
                {Object.entries(statistics.absence_by_teacher)
                  .sort((a, b) => b[1] - a[1])
                  .map(([teacher, count]) => (
                    <div key={teacher} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                      <span className="text-sm font-medium text-slate-700">{teacher}</span>
                      <span className="px-3 py-1 bg-red-100 text-red-900 text-sm font-semibold rounded">{count}</span>
                    </div>
                  ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
